const { generateSingletonExports } = require('../common/utils/singleton');
const showInteraction = require('../interactions/show');
const forms = require('./forms');
const { addDocumentDelegatedListener } = require('../common/utils/domEvents');

const textareaValidityForcedError = {
  valid: false,
  patternMismatch: true,
};

/**
 * Test textarea data-pattern regex, since pattern is not part of textarea element
 * When it doesn't match we set data-has-pattern-error
 * Otherwise, we remove data-has-pattern-error and the error feedback text
 * @param {event} evt Event object
 */
const setTextareaPatternMismatch = (evt) => {
  const textarea = evt.target;
  const pattern = $(textarea).data('pattern');
  const regex = new RegExp(pattern, 'gu');
  const matches = textarea.value.match(regex);

  if (!matches) {
    textarea.setAttribute('data-has-pattern-error', true);
  } else {
    textarea.removeAttribute('data-has-pattern-error');
    $(textarea).siblings('.invalid-feedback').text('');
  }
};

/**
 * Remove all spaces from string
 * @param {string} str - String to be cleaned
 * @returns {string} space trimmed string
 */
const removeAllSpaces = (str) => str.replace(/\s+/g, '');

/**
 * Sanitizes Telephone field by removing all spaces
 * @param {event} evt - event callback body
 * @returns {void}
 */
const sanitizeTelephoneField = (evt) => {
  const field = evt.target;

  if (field?.value) {
    field.value = removeAllSpaces(field.value);
  }
};

/**
 * Sanitizes field by removing leading, trailing spaces
 * @param {event} evt - event callback body
 * @returns {void}
 */
const sanitizeTextField = (evt) => {
  const field = evt.target;

  if (field?.value) {
    field.value = field.value.trim();
    field.dispatchEvent(new Event('input'));
  }
};

/** General & complex function to check visibility on some validation events
 * Do not export nor use externally
 * @param  {HTMLElement} elem - Element to check visibility
 * @returns {boolean} true if visible, false otherwise
 */
function isElementVisible(elem) {
  if (!(elem instanceof Element)) {
    return true;
  }

  // Custom BYL validation
  const closestInteractionShow = elem.closest('.interaction--show');
  if (
    closestInteractionShow &&
    !showInteraction.isShown(closestInteractionShow)
  ) {
    return false;
  }

  // Standard validation
  const style = getComputedStyle(elem);

  const isElementHiddenByStyles =
    style.display === 'none' ||
    style.visibility !== 'visible' ||
    style.opacity < 0.1;
  const elementHasNoSize =
    elem.offsetWidth +
      elem.offsetHeight +
      elem.getBoundingClientRect().height +
      elem.getBoundingClientRect().width ===
    0;
  if (isElementHiddenByStyles || elementHasNoSize) {
    return false;
  }

  if (elem.tagName === 'FORM') {
    return true;
  }

  const parent = elem.parentNode;
  if (parent) {
    return isElementVisible(parent);
  }

  return true;
}

/**
 * Returns true if textarea has pattern mismatch.
 * @param {HTMLElement} textarea - the textarea element
 * @returns {boolean} True if textarea has pattern mismatch, False otherwise.
 */
function textareaHasPatternError(textarea) {
  if (textarea) {
    return !!textarea.dataset.hasPatternError;
  }
  return false;
}

/**
 * Validate some fields independently from the form.
 * @param {HTMLElement[]} fields - An array of HTMLElement fields to be validated
 * @param {boolean} [validateHidden=true] - A boolean indicating if it should validate hidden fields.
 * @returns {boolean} - Flag to indicate if fields are valid
 */
function validateFields(fields, validateHidden = true) {
  if (!fields?.length || fields.length === 0) {
    return false;
  }

  let valid = true;
  fields.forEach((field) => {
    /** @type {ValidityState} https://developer.mozilla.org/en-US/docs/Web/API/ValidityState */
    let validityState;

    const isTextArea = field.tagName.toLowerCase() === 'textarea';
    if (isTextArea && textareaHasPatternError(field)) {
      validityState = textareaValidityForcedError;
    }

    if (field.checkValidity && !field.checkValidity()) {
      validityState = field.validity;
    }

    let isInvalid = !!validityState;
    if (isInvalid) {
      const shouldValidate = validateHidden || isElementVisible(field);
      const shouldIgnore = !shouldValidate;
      if (shouldIgnore) {
        isInvalid = false;
      }
    }

    if (isInvalid) {
      valid = false;
      $(field).trigger('invalid', validityState);
    }
  });
  return valid;
}

/**
 * Validate whole form. Requires `this` to be set to form object
 * (an HTMLFormElement, not a jQuery object)
 * @param {jQuery.event} event - Event to be canceled if form is invalid.
 * @returns {boolean} - Flag to indicate if form is valid
 */
function validateForm(event) {
  const form = this;
  // sometimes we're calling this from the window object
  if (form?.tagName?.toLowerCase() !== 'form') {
    return false;
  }

  let valid = true;
  const formTextareas = form.getElementsByTagName('textarea');
  let i;
  for (i = 0; i < formTextareas.length; i++) {
    const textarea = formTextareas[i];
    const textareaMismatch = textareaHasPatternError(textarea);
    if (textareaMismatch) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      valid = false;
      $(textarea).trigger('invalid', textareaValidityForcedError);
    }
  }

  if (form.checkValidity && !form.checkValidity()) {
    // Data attr data-validate-hidden allows to validate hidden fields in forms
    const validateHidden = $(form).data('validate-hidden');
    $(form)
      .find('input, select, textarea')
      .each(function triggerInvalidEventIfNotValid() {
        const shouldValidate = validateHidden
          ? !this.validity.valid
          : !this.validity.valid && isElementVisible(this);
        if (shouldValidate) {
          // we move this valid flag inside the isVisible checks, otherwise
          // we were returning form as invalid due to hidden empty fields
          valid = false;
          // we also need to prevent after this check, not earlier
          if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
          }
          $(this).trigger('invalid', this.validity);
        }
      });
  }

  return valid;
}

/**
 * @param {event} e Event object
 */
function validateTextAreaPattern(e) {
  const element = e.target;

  if (element.type === 'textarea') {
    setTextareaPatternMismatch(e);
    if (textareaHasPatternError(element)) {
      $(element).trigger('invalid', textareaValidityForcedError);
    }
  }
}

/**
 * Check if any required field is empty
 * Required radio buttons and checkboxes are not managed this way,
 * as they have to show an error instead of blocking submit state
 * @param {HTMLElement} form form element
 * @returns {boolean} true if any required visible field is empty/not checked, false otherwise
 */
function isRequiredEmpty(form) {
  const requiredElements = form.querySelectorAll(
    'input[required]:not([type=radio],[type=checkbox]),textarea[required],select[required]'
  );
  let isEmpty = false;
  let i = 0;
  while (!isEmpty && i < requiredElements.length) {
    const element = requiredElements[i];
    if (isElementVisible(element) && element.value.length === 0) {
      isEmpty = true;
    }

    i += 1;
  }
  return isEmpty;
}

/**
 * Set the submit buttons' state after an Event
 */
function handleSubmitState() {
  /** @type {HTMLElement} */
  const form =
    this.tagName.toLowerCase() === 'form' ? this : this.closest('form');
  const isEmpty = isRequiredEmpty(form);
  const submitButtons = form.querySelectorAll(
    '[type=submit],[data-fake-submit="true"]'
  );
  submitButtons.forEach((button) => {
    if (isEmpty) {
      button.classList.add('disabled');
    } else {
      button.classList.remove('disabled');
    }
    if (button.classList.contains('interaction--click-button-desktop')) {
      button.classList.add('interaction--enable-disable-desktop-form-v3');
    }
  });
}

/**
 * @param {event} e Event Object
 * @param {Object} [data] Data object
 * @param {boolean} [data.valid] Is valid
 * @param {boolean} [data.patternMismatch] Pattern mismatch
 */
function handleInvalidEventOfFormControl(e, data) {
  // We can validate inputs within or outside forms.
  // Solitary inputs should only be used to provide a UI.

  e.preventDefault();
  this.setCustomValidity('');

  let textareaPatternMismatch = false;
  if (
    e.target.nodeName === 'TEXTAREA' &&
    data?.valid === false &&
    data?.patternMismatch === true
  ) {
    textareaPatternMismatch = true;
  }

  if (!this.validity.valid || textareaPatternMismatch) {
    let { validationMessage } = this;
    if (
      (this.validity.patternMismatch ||
        this.validity.typeMismatch ||
        textareaPatternMismatch) &&
      $(this).data('pattern-mismatch')
    ) {
      validationMessage = $(this).data('pattern-mismatch');
    }
    if (
      (this.validity.rangeOverflow || this.validity.rangeUnderflow) &&
      $(this).data('range-error')
    ) {
      validationMessage = $(this).data('range-error');
    }
    if (
      (this.validity.tooLong || this.validity.tooShort) &&
      $(this).data('range-error')
    ) {
      validationMessage = $(this).data('range-error');
    }
    if (this.validity.valueMissing && $(this).data('missing-error')) {
      validationMessage = $(this).data('missing-error');
    }

    forms.addFieldError($(this), validationMessage);
  }
}

/**
 * Handle the 'clearErrors' command that is delegated to body.
 * @param {jQuery.Event} evt The jQuery event object
 */
function handleClearErrorsCommand(evt) {
  const { target } = evt;
  const form = target.matches('form') ? target : target.closest('form');
  forms.clearFormErrors(form);
}

/**
 * Handle the 'validateForm' command that is delegated to body.
 * @param {jQuery.Event} evt The jQuery event object
 */
function handleValidateFormCommand(evt) {
  const { target } = evt;
  const form = target.matches('form') ? target : target.closest('form');
  const isValid = validateForm.call(form, null);
  $(form).data('isValid', isValid);
}

let initialized = false;

const publicModule = {
  addListeners: {
    clearErrorsOnSubmit() {
      $('body').on(
        'click',
        'form button[type="submit"], form input[type="submit"]',
        function onSubmitClick() {
          // clear all errors when trying to submit the form
          forms.clearFormErrors($(this).parents('form'));
        }
      );
    },
    clearFieldErrorListeners() {
      /**
       * @param {event} event Event Object
       */
      function handleFieldError(event) {
        const { target } = event;
        forms.clearError(target);
      }

      addDocumentDelegatedListener(
        'keyup',
        'form input, form textarea',
        handleFieldError
      );
      addDocumentDelegatedListener(
        'change',
        'form input, form select',
        handleFieldError
      );
    },
    listenFormCommands() {
      $('body').on('validateForm', handleValidateFormCommand);
      $('body').on('clearErrors', handleClearErrorsCommand);
    },
    listenInvalidFormControls() {
      $('body').on(
        'invalid',
        'form input, form select, form textarea, .field-without-form input, .field-without-form select, .field-without-form textarea',
        handleInvalidEventOfFormControl
      );
    },
    sanitizeFieldsOnBlur() {
      $('body').on('blur', 'input[type="tel"]', sanitizeTelephoneField);
      $('body').on('blur', 'input[type="text"]', sanitizeTextField);
    },
    testTextareaPatternRegexOnBlur() {
      // pattern isn't part of textarea HTML5 validation system, we need to test it on blur
      $('body').on('blur', 'textarea', setTextareaPatternMismatch);
    },
    updateSubmitButtonState() {
      addDocumentDelegatedListener(
        'keyup',
        'form input, form textarea',
        handleSubmitState
      );
      addDocumentDelegatedListener(
        'change',
        'form input, form select',
        handleSubmitState
      );
    },
  },

  submit() {
    // override the SFRA submit, we are moving it to each form JS
  },

  submitDisable($forms = undefined) {
    const safeForms = $forms || $('form');
    safeForms.each(function validateSubmit() {
      handleSubmitState.call(this);
    });
  },

  /**
   * Handle the submit state for the given form
   * @param {HTMLElement} form Form element
   */
  handleSubmitState(form) {
    handleSubmitState.call(form);
  },

  functions: {
    validateFields,
    /**
     * Validate a form
     * @param {HTMLFormElement} form The form to validate
     * @param {Event} event The event, usually a 'submit'
     * @returns {boolean} - Flag to indicate if form is valid. True if valid.
     */
    validateForm(form, event) {
      return validateForm.call(form, event || null);
    },
    /**
     * Validate a form with native HTML5 validation (+textarea pattern).
     * There is no event associated to cancel in case the form is not valid.
     * @param {HTMLFormElement} form The form to validate
     * @returns {boolean} - Flag to indicate if form is valid. True if valid.
     */
    validateFormatData(form) {
      return validateForm.call(form, null);
    },
    validateTextAreaPattern,
  },

  init() {
    this.submitDisable();
    if (initialized) return;
    this.addListeners.clearErrorsOnSubmit();
    this.addListeners.clearFieldErrorListeners();
    this.addListeners.listenFormCommands();
    this.addListeners.listenInvalidFormControls();
    this.addListeners.sanitizeFieldsOnBlur();
    this.addListeners.testTextareaPatternRegexOnBlur();
    this.addListeners.updateSubmitButtonState();
    initialized = true;
  },
};

module.exports = generateSingletonExports('clientSideValidation', publicModule);
